import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDownIcon,
  User01Icon,
  Settings01Icon,
  Flag01Icon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons/button'
import { TextArea } from '@/components/base/textarea/textarea'
import type { Message, PartnerReview, ToolCall } from '@/types/conversation'
import { ToolCallScreenshotLink } from './ToolCallScreenshotLink'

const MAX_REVIEW_COMMENT_LENGTH = 500

export interface TranscriptReviewProps {
  /** Raw transcript indices of assistant turns that can be reviewed */
  reviewableIndices: number[]
  /** Existing reviews, keyed by raw transcript index via message_index */
  reviews: PartnerReview[]
  /** Submits one review; should throw on failure so the form stays open */
  onSubmit: (input: {
    messageIndex: number
    flagged: boolean
    comment: string
  }) => Promise<void>
}

interface ConversationTranscriptProps {
  messages?: Message[] | string
  className?: string
  /** Assistant configuration (for checking screenshot support) */
  assistantConfig?: unknown
  /** Date when the conversation took place (for screenshot URL generation) */
  conversationDate?: string
  /** Per-turn review controls; only provided by the conversation drawer for admins */
  review?: TranscriptReviewProps
}

interface DisplayMessage {
  message: Message
  /** Index in the raw messages array, used to address a turn in reviews */
  rawIndex: number
}

// Simple AI avatar component
function AIAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500">
      <span className="text-xs font-bold text-white">AI</span>
    </div>
  )
}

function UserAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900">
      <User01Icon className="h-4 w-4 text-blue-500" />
    </div>
  )
}

function SystemAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900">
      <Settings01Icon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
    </div>
  )
}

interface ToolCallDisplayProps {
  toolCall: ToolCall
  toolResponse?: Message
  /** Assistant configuration (for checking screenshot support) */
  assistantConfig?: unknown
  /** Date when the conversation took place (for screenshot URL generation) */
  conversationDate?: string
}

function ToolCallDisplay({
  toolCall,
  toolResponse,
  assistantConfig,
  conversationDate,
}: ToolCallDisplayProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  let parsedArgs: Record<string, unknown> = {}
  try {
    parsedArgs = JSON.parse(toolCall.function.arguments)
  } catch {
    // If parsing fails, show raw arguments
  }

  return (
    <div className="mt-2 rounded-md bg-brand-50 p-2 dark:bg-brand-950">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-left text-sm"
      >
        <span className="font-medium text-brand-700 dark:text-brand-300">
          {toolCall.function.name}
        </span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 transition-transform text-brand-600',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && (
        <div className="mt-2 space-y-2">
          <div className="rounded border border-brand-200 bg-white p-2 dark:border-brand-800 dark:bg-gray-900">
            <pre className="whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">
              {Object.keys(parsedArgs).length > 0
                ? Object.entries(parsedArgs)
                    .map(
                      ([key, value]) =>
                        `${key}: ${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}`
                    )
                    .join('\n')
                : toolCall.function.arguments}
            </pre>
          </div>
          {toolResponse && (
            <div className="rounded border-l-2 border-brand-500 bg-white p-2 dark:bg-gray-900">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('resources.conversations.transcript.response')}
              </span>
              <p className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">
                {toolResponse.content}
              </p>
              {/* Screenshot link for availability tool calls */}
              <ToolCallScreenshotLink
                toolCallId={toolCall.id}
                toolName={toolCall.function.name}
                conversationDate={conversationDate}
                assistantConfig={assistantConfig}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface ReviewFormProps {
  existingReview?: PartnerReview
  onCancel: () => void
  /** Should throw on failure so the form stays open for a retry */
  onSubmit: (flagged: boolean, comment: string) => Promise<void>
}

function ReviewForm({ existingReview, onCancel, onSubmit }: ReviewFormProps) {
  const { t } = useTranslation()
  const [comment, setComment] = useState(existingReview?.comment ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = async (flagged: boolean, value: string) => {
    setIsSubmitting(true)
    try {
      await onSubmit(flagged, value)
    } catch {
      // Error toast is shown by the caller; keep the form open for a retry
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full min-w-64 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
      <TextArea
        value={comment}
        onChange={setComment}
        maxLength={MAX_REVIEW_COMMENT_LENGTH}
        rows={2}
        autoFocus
        placeholder={t(
          'resources.conversations.transcript.review.comment_placeholder'
        )}
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-400">
          {comment.length}/{MAX_REVIEW_COMMENT_LENGTH}
        </span>
        <div className="flex items-center gap-2">
          {existingReview?.flagged && (
            <Button
              color="tertiary"
              size="sm"
              isDisabled={isSubmitting}
              onClick={() => submit(false, '')}
            >
              {t('resources.conversations.transcript.review.remove')}
            </Button>
          )}
          <Button
            color="tertiary"
            size="sm"
            isDisabled={isSubmitting}
            onClick={onCancel}
          >
            {t('resources.conversations.transcript.review.cancel')}
          </Button>
          <Button
            color="primary-destructive"
            size="sm"
            isDisabled={!comment.trim() || isSubmitting}
            isLoading={isSubmitting}
            onClick={() => submit(true, comment.trim())}
          >
            {t('resources.conversations.transcript.review.submit')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function ConversationTranscript({
  messages,
  className,
  assistantConfig,
  conversationDate,
  review,
}: ConversationTranscriptProps) {
  const { t } = useTranslation()
  const [openReviewIndex, setOpenReviewIndex] = useState<number | null>(null)

  const parsedMessages = useMemo(() => {
    if (!messages) return []
    if (Array.isArray(messages)) return messages

    try {
      return JSON.parse(messages)
    } catch {
      return []
    }
  }, [messages])

  // Filter and process messages, keeping the raw array index for review addressing
  const displayMessages = useMemo<DisplayMessage[]>(() => {
    if (!parsedMessages.length) return []

    // Skip first message (usually system prompt) and filter empties
    return parsedMessages
      .map((message: Message, rawIndex: number) => ({ message, rawIndex }))
      .slice(1)
      .filter(
        ({ message }: DisplayMessage) =>
          message &&
          ((message.content !== '' &&
            message.content !== "''" &&
            message.content !== '""') ||
            message.tool_calls)
      )
  }, [parsedMessages])

  const reviewByIndex = useMemo(() => {
    const map = new Map<number, PartnerReview>()
    review?.reviews.forEach((entry) => map.set(entry.message_index, entry))
    return map
  }, [review?.reviews])

  // Helper to truncate after "The call has ended"
  const truncateAfterEnd = (content: string | null) => {
    if (!content) return content
    const baseText = 'The call has ended'
    const endIndex = content.indexOf(baseText)
    if (endIndex !== -1) {
      return content.substring(0, endIndex + baseText.length).trim()
    }
    return content
  }

  if (!displayMessages.length) {
    return (
      <div className={cn('text-center text-gray-500 py-8', className)}>
        {t('resources.conversations.no_transcript_available')}
      </div>
    )
  }

  const flaggedCount = review
    ? review.reviews.filter((entry) => entry.flagged).length
    : 0

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {t('resources.conversations.tabs.transcript')}
      </h3>

      {review && (
        <div className="flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-200">
          <Flag01Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t('resources.conversations.transcript.review.banner')}
            {flaggedCount > 0 &&
              ` · ${t('resources.conversations.transcript.review.banner_flagged', { count: flaggedCount })}`}
          </span>
        </div>
      )}

      <div className="space-y-4">
        {displayMessages.map(({ message, rawIndex }) => {
          // Skip tool responses as they'll be shown with their tool calls
          if (message.role === 'tool' && message.tool_call_id) {
            return null
          }

          const isUser = message.role === 'user'
          const isSystem = message.role === 'system'
          const isAssistant = message.role === 'assistant'
          const hasToolCalls = message.tool_calls && message.tool_calls.length > 0

          const existingReview = review ? reviewByIndex.get(rawIndex) : undefined
          const isReviewable = Boolean(
            review &&
              isAssistant &&
              (review.reviewableIndices.includes(rawIndex) || existingReview)
          )
          const isFlagged = existingReview?.flagged ?? false

          return (
            <div
              key={`message-${rawIndex}`}
              className={cn(
                'flex gap-3',
                isUser ? 'flex-row' : 'flex-row-reverse'
              )}
            >
              {/* Avatar */}
              {isSystem ? (
                <SystemAvatar />
              ) : isAssistant || hasToolCalls ? (
                <AIAvatar />
              ) : (
                <UserAvatar />
              )}

              {/* Message bubble with optional review form below it */}
              <div
                className={cn(
                  'flex max-w-[80%] flex-col gap-2',
                  isUser ? 'items-start' : 'items-end'
                )}
              >
                <div
                  className={cn(
                    'rounded-lg p-3',
                    isUser
                      ? 'bg-gray-100 dark:bg-gray-800'
                      : hasToolCalls
                        ? 'bg-brand-100 dark:bg-brand-900'
                        : 'bg-brand-500 text-white',
                    isFlagged && 'ring-2 ring-red-500'
                  )}
                >
                  {message.content && (
                    <p
                      className={cn(
                        'text-sm',
                        isUser
                          ? 'text-gray-900 dark:text-gray-100'
                          : hasToolCalls
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-white'
                      )}
                    >
                      {truncateAfterEnd(message.content)}
                    </p>
                  )}

                  {/* Tool calls */}
                  {hasToolCalls &&
                    message.tool_calls!.map((toolCall) => {
                      const toolResponse = displayMessages.find(
                        (entry) =>
                          entry.message.role === 'tool' &&
                          entry.message.tool_call_id === toolCall.id
                      )?.message
                      return (
                        <ToolCallDisplay
                          key={toolCall.id}
                          toolCall={toolCall}
                          toolResponse={toolResponse}
                          assistantConfig={assistantConfig}
                          conversationDate={conversationDate}
                        />
                      )
                    })}
                </div>

                {isFlagged &&
                  openReviewIndex !== rawIndex &&
                  existingReview?.comment && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {existingReview.comment}
                    </p>
                  )}

                {review && isReviewable && openReviewIndex === rawIndex && (
                  <ReviewForm
                    existingReview={existingReview}
                    onCancel={() => setOpenReviewIndex(null)}
                    onSubmit={async (flagged, comment) => {
                      await review.onSubmit({
                        messageIndex: rawIndex,
                        flagged,
                        comment,
                      })
                      setOpenReviewIndex(null)
                    }}
                  />
                )}
              </div>

              {/* Review flag trigger */}
              {isReviewable && (
                <button
                  type="button"
                  onClick={() =>
                    setOpenReviewIndex(
                      openReviewIndex === rawIndex ? null : rawIndex
                    )
                  }
                  title={t('resources.conversations.transcript.review.flag')}
                  className={cn(
                    'self-center rounded p-1 transition-colors',
                    isFlagged
                      ? 'text-red-500'
                      : 'text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400'
                  )}
                >
                  <Flag01Icon className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">
                    {t('resources.conversations.transcript.review.flag')}
                  </span>
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ConversationTranscript
